import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import ParentHeader from "../../Parent/ParentHeader/ParentHeader";
import { RegSteps, ActionBar, PageHeader, Msr, useMaterialSymbols, stepsFor } from "./components";
import SoloEntries from "./SoloEntries";
import Teams from "./Teams";
import Assessment from "./Assessment";
import CheckoutReview from "./CheckoutReview";
import { getOireachtasEvent, getCompetitions, getReference, getRoster, getCart, getTeams, saveCart, quoteCart, setRosterFlag, oireachtasCheckout } from "./api";
import { rowsToCartItems, cartItemsToRows } from "./cascade";
import { validateTeams, blockingTeamErrors, isTeamComp } from "./teamRules";
import { regWindow, regWindowState, lateFeeTag, closeTimeLabel } from "./deadlines";
import { parseMoneyCents, paymentAmountError } from "./instalments";
import moment from "moment";
import { formatDate } from "../../helper/formatDate";
import "./oireachtas.css";

// The teacher registration wizard (T2 -> T4 -> [T5 if the event has an
// assessment] -> T6 -> pay). Steps derive from the event (components.jsx
// stepsFor). Drafts (solo rows + teams + reclaim quantities) autosave
// server-side; pricing is ALWAYS the server's (quote/checkout) — this UI
// never computes money it charges (T5's live math is display-only).

export default function RegistrationShell() {
  useMaterialSymbols();
  const { id } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = React.useState("solo");
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  const [event, setEvent] = React.useState(null);
  const [competitions, setCompetitions] = React.useState([]);
  const [reference, setReference] = React.useState({});
  const [roster, setRoster] = React.useState([]);
  const [showHidden, setShowHidden] = React.useState(false);
  const [showIncomplete, setShowIncomplete] = React.useState(false);
  const [rows, setRows] = React.useState({});
  const [teamRows, setTeamRows] = React.useState([]); // cart-shaped draft teams (M4)
  const [savedTeams, setSavedTeams] = React.useState([]); // already-registered teams
  const [reclaimRows, setReclaimRows] = React.useState([]); // [{itemKey, qty}] (M5)
  const [saveState, setSaveState] = React.useState("saved"); // saved | saving | dirty
  const [quote, setQuote] = React.useState(null);
  const [quoting, setQuoting] = React.useState(false);
  const [paying, setPaying] = React.useState(false);
  const [payError, setPayError] = React.useState(null);
  // "Pay in full" vs "Pay in instalments" + the first payment typed at checkout.
  const [payChoice, setPayChoice] = React.useState({ mode: "full", amountText: "" });

  const compsById = React.useMemo(
    () => Object.fromEntries(competitions.map((c) => [String(c._id), c])),
    [competitions]
  );
  const setsByKey = React.useMemo(
    () => Object.fromEntries((reference.setDances || []).map((s) => [s.key, s])),
    [reference]
  );

  // initial load
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [ev, comps, ref, ros, cart, regTeams] = await Promise.all([
          getOireachtasEvent(id),
          getCompetitions(id),
          getReference(),
          getRoster(id, true),
          getCart(id),
          getTeams(id).catch(() => []),
        ]);
        if (!alive) return;
        setEvent(ev);
        const compList = comps || [];
        setCompetitions(compList);
        setReference(ref);
        setRoster(ros || []);
        setSavedTeams(regTeams || []);
        const items = cart && cart.items;
        if (items && items.length) {
          setRows(cartItemsToRows(
            items,
            Object.fromEntries(compList.map((c) => [String(c._id), c])),
            Object.fromEntries(((ref && ref.setDances) || []).map((s) => [s.key, s])),
            ref,
            Object.fromEntries((ros || []).map((d) => [String(d._id), d]))
          ));
        }
        if (cart && cart.teams && cart.teams.length) setTeamRows(cart.teams);
        if (cart && cart.reclaimAllocations && cart.reclaimAllocations.length) {
          // Drop allocations whose item no longer exists on the event — a
          // draft saved before a reclaimItems swap carries orphaned keys, and
          // rehydrating them would write them straight back on the next
          // autosave. Filtering here self-heals the draft.
          const knownKeys = new Set(
            ((ev && ev.reclaimItems) || []).map((i) => String(i.key))
          );
          setReclaimRows(
            cart.reclaimAllocations
              .filter((a) => a && a.itemKey && knownKeys.has(String(a.itemKey)))
              .map((a) => ({ itemKey: String(a.itemKey), qty: Number(a.qty) || 0 }))
          );
        }
      } catch (e) {
        if (alive) setLoadError(e.messages || "Could not load the event.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  // debounced server-side draft autosave (solo rows + teams share one cart)
  const dancersById = React.useMemo(
    () => Object.fromEntries(roster.map((d) => [String(d._id), d])),
    [roster]
  );
  const items = React.useMemo(
    () => rowsToCartItems(rows, compsById, setsByKey, { competitions, dancersById }),
    [rows, compsById, setsByKey, competitions, dancersById]
  );
  const itemsRef = React.useRef(items);
  itemsRef.current = items;
  const teamsRef = React.useRef(teamRows);
  teamsRef.current = teamRows;
  const reclaimRef = React.useRef(reclaimRows);
  reclaimRef.current = reclaimRows;
  const eventRef = React.useRef(event);
  eventRef.current = event;
  const firstRun = React.useRef(true);
  React.useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    // Closed events are read-only (§11/G2): never autosave a draft the
    // checkout gate would refuse anyway. The lock card suppresses editing UI;
    // this is the belt for anything that still patches state.
    if (eventRef.current && regWindow(eventRef.current).state === "closed") return;
    setSaveState("dirty");
    const t = setTimeout(async () => {
      setSaveState("saving");
      try {
        await saveCart(id, itemsRef.current, teamsRef.current, reclaimRef.current);
        setSaveState("saved");
      } catch (e) {
        setSaveState("dirty");
      }
    }, 900);
    return () => clearTimeout(t);
  }, [rows, teamRows, reclaimRows, id]);

  const patchRow = (dancerId, p) =>
    setRows((r) => ({ ...r, [dancerId]: { ...(r[dancerId] || { dancer_id: dancerId }), dancer_id: dancerId, ...p } }));

  const hideDancer = async (d) => {
    setRows((r) => { const n = { ...r }; delete n[String(d._id)]; return n; });
    setRoster((ros) => ros.map((x) => (x._id === d._id ? { ...x, hidden: true } : x)));
    try { await setRosterFlag(d._id, true); } catch (e) { /* roster reload will reconcile */ }
  };
  const restoreDancer = async (d) => {
    setRoster((ros) => ros.map((x) => (x._id === d._id ? { ...x, hidden: false } : x)));
    try { await setRosterFlag(d._id, false); } catch (e) { /* roster reload will reconcile */ }
  };

  // entering assessment or checkout -> flush the draft, then quote server-side
  // (T5 reads its pool/prior figures off breakdown.reclaim; T6 the whole thing)
  const goQuotedStep = async (key) => {
    setStep(key);
    setQuoting(true);
    setPayError(null);
    try {
      // Closed events are read-only: quote (for display) without flushing the
      // draft — same rule as the autosave suppression above.
      if (!(event && regWindow(event).state === "closed")) {
        await saveCart(id, itemsRef.current, teamsRef.current, reclaimRef.current);
        setSaveState("saved");
      }
      const q = await quoteCart(id, itemsRef.current, teamsRef.current, reclaimRef.current);
      setQuote(q);
    } catch (e) {
      setQuote({ breakdown: null, items: [], guard: { ok: false, errors: [{ message: e.messages || "Could not price the cart." }] } });
    } finally {
      setQuoting(false);
    }
  };
  const goStep = (key) =>
    key === "checkout" || key === "assessment" ? goQuotedStep(key) : setStep(key);

  // "Pay in instalments" (card 15862648): only when the quote offers it.
  const instalmentsOn = !!(
    payChoice.mode === "instalments" && quote && quote.instalments && quote.instalments.available
  );
  const firstCents = instalmentsOn ? parseMoneyCents(payChoice.amountText) : null;
  const totalDue = (quote && quote.breakdown && quote.breakdown.totalCents) || 0;
  const planError = instalmentsOn
    ? paymentAmountError(firstCents, totalDue, quote.instalments.minPaymentCents)
    : null;

  // pay: create the PaymentIntent, hand off to the shared Stripe page
  const pay = async () => {
    setPaying(true);
    setPayError(null);
    try {
      const cart = await saveCart(id, itemsRef.current, teamsRef.current, reclaimRef.current);
      // Paying the whole total "in instalments" is just paying in full.
      const plan = instalmentsOn && firstCents < totalDue
        ? { plan: "instalments", firstPaymentCents: firstCents }
        : null;
      const resp = await oireachtasCheckout(id, cart._id, plan);
      const clientSecret = resp.checkout && resp.checkout.client_secret;
      if (!clientSecret) {
        // zero-total checkout converted without a charge
        navigate(`/teacher/oireachtas/${id}/register/success`);
        return;
      }
      navigate("/checkout", {
        state: {
          from: "oireachtasReg",
          oireachtasId: id,
          paymentDetails: {
            showPaymentPage: true,
            client: clientSecret,
            errors: "",
            // what the card is charged now: the first instalment or the total,
            // plus the server's FROZEN plan figures (never re-derived client-side)
            amountCents: resp.amountCents,
            kind: resp.plan ? "firstInstalment" : "full",
            totalCents: resp.plan ? resp.plan.totalCents : undefined,
            balanceAfterCents: resp.plan ? resp.plan.balanceAfterCents : undefined,
            balanceDueAt: resp.plan ? resp.plan.balanceDueAt : undefined,
          },
          eventDetails: {
            eventType: "Oireachtas",
            eventName: event && event.name,
            eventDate: event && event.eventDays && event.eventDays[0],
            eventImage: event && event.logo,
          },
        },
      });
    } catch (e) {
      // The checkout gate 400s with a machine code once entries close (§11);
      // surface that as the lock state, not a generic validation failure.
      const code = e && e.error && e.error.response && e.error.response.data && e.error.response.data.code;
      if (code === "ENTRIES_CLOSED") {
        setPayError("Entries are closed for this event — payment is no longer possible.");
        // refresh the quote so the server's deadline state drives the lock UI
        quoteCart(id, itemsRef.current, teamsRef.current, reclaimRef.current)
          .then(setQuote)
          .catch(() => {});
      } else {
        setPayError(Array.isArray(e.messages) ? e.messages.join("; ") : e.messages || "Checkout failed.");
      }
      setPaying(false);
    }
  };

  // live team validation for the step gate (client mirror; server re-enforces)
  const teamBlocking = React.useMemo(() => {
    if (!teamRows.length) return [];
    const result = validateTeams({
      teams: teamRows,
      competitionsById: compsById,
      dancersById: Object.fromEntries(roster.map((d) => [String(d._id), d])),
      ceiliDancesByKey: Object.fromEntries((reference.ceiliDances || []).map((d) => [d.key, d])),
      savedTeams: savedTeams.map((t) => ({
        _id: t._id, competition_id: t.competition && t.competition._id, members: t.members || [],
      })),
      teamRules: event && event.teamRules,
    });
    return blockingTeamErrors(teamRows, result.errors);
  }, [teamRows, compsById, roster, reference, savedTeams, event]);

  const hasTeamComps = React.useMemo(() => competitions.some(isTeamComp), [competitions]);
  const completeCount = items.length;
  const completeIds = new Set(items.map((item) => String(item.dancer_id)));
  const incompleteIds = Object.values(rows)
    .filter((row) => (row.primaryId || row.treble === "add" || row.tradSet === "add") &&
      !completeIds.has(String(row.dancer_id)))
    .map((row) => String(row.dancer_id));
  const teamCount = teamRows.length + savedTeams.length;
  const status = (
    <>
      <span>{completeCount} dancer{completeCount === 1 ? "" : "s"}{saveState === "saved" ? " saved" : " ready"}</span>
      {incompleteIds.length > 0 && (
        <button type="button" className="solo-incomplete-link" onClick={() => { setShowIncomplete(true); setStep("solo"); }}>
          {incompleteIds.length} incomplete — review
        </button>
      )}
      <span className="dot"></span>
      <span>{teamCount} team{teamCount === 1 ? "" : "s"}</span>
      <span className="dot"></span>
      <span className={"sv" + (incompleteIds.length ? " solo-save-warning" : "")} role="status">
        <Msr>{incompleteIds.length ? "warning" : saveState === "saved" ? "cloud_done" : "cloud_upload"}</Msr>
        {saveState === "saved" ? (incompleteIds.length ? "Incomplete entries not saved" : "Draft saved") : saveState === "saving" ? "Saving…" : "Unsaved changes"}
      </span>
    </>
  );

  if (loading) return <div className="oir"><ParentHeader /><div className="loading">Loading registration…</div></div>;
  if (loadError) return <div className="oir"><ParentHeader /><div className="loading">{loadError}</div></div>;

  // Lock state (§11/G2). Server-authoritative once a quote is loaded
  // (quote.deadlines); the client mirror covers the pre-quote render. Closed
  // => read-only: lock card, editing suppressed, autosave + pay disabled.
  const winState = regWindowState(event, quote);
  const isClosed = winState === "closed";
  const isLate = winState === "late";

  const steps = stepsFor(event);
  const stepIdx = steps.findIndex((s) => s.key === step);
  const nextStep = steps[stepIdx + 1];
  const prevStep = steps[stepIdx - 1];
  const guardOk =
    !quote ||
    ((quote.guard && quote.guard.ok) &&
      !(quote.teamGuard && quote.teamGuard.blocking && quote.teamGuard.blocking.length));
  // zero-total checkouts are legitimate (everything already billed / pool-
  // covered allocations) — gate on having anything NEW, not on the amount.
  const newAllocated = reclaimRows.some((r) => r.qty > 0);
  const hasNew =
    quote &&
    ((quote.items && quote.items.length > 0) ||
      (quote.teams && quote.teams.length > 0) ||
      (quote.breakdown && quote.breakdown.reclaim && quote.breakdown.reclaim.newAllocatedCents > 0));
  // per-step Next gate: solo -> teams needs anything to eventually check out
  // (or team comps to build); teams blocks on unresolved violations;
  // assessment never blocks (sponsorship auto-covers the minimum, D2).
  const hasAssessmentStep = steps.some((s) => s.key === "assessment");
  const nextDisabled =
    step === "solo"
      ? completeCount === 0 && teamRows.length === 0 && !hasTeamComps && !hasAssessmentStep
      : step === "teams"
        ? teamBlocking.length > 0 ||
          (completeCount === 0 && teamRows.length === 0 && !hasAssessmentStep && !newAllocated)
        : false;

  return (
    <div className="oir">
      <ParentHeader />
      <div className={"wrap" + (step === "checkout" ? " narrow" : "")}>
        <PageHeader event={event} school={roster.length && roster[0].schoolName} />
        <RegSteps steps={steps} current={step} onGo={goStep} />
        {isClosed && (
          <div className="reg-lock-card">
            <Msr>lock</Msr>
            <div>
              <div className="main">Registration is closed</div>
              <div className="sub">
                The entry window for this Oireachtas has ended — entries can no longer be
                added or changed. Everything below is read-only.
              </div>
            </div>
            <button type="button" className="bigbtn ghost" onClick={() => navigate(`/teacher/oireachtas/${id}/entries`)}>
              View current entries
            </button>
          </div>
        )}
        {!isClosed && isLate && (
          <div className="reg-late-strip">
            <Msr>warning</Msr>
            <span>
              Late entries until {moment(formatDate(event.lateEntryCloseDate)).format("MMMM D, YYYY")} at {closeTimeLabel(event, event.lateEntryCloseDate)}
              {lateFeeTag(event) ? ` — ${lateFeeTag(event)} applies to entries in this window.` : "."}
            </span>
          </div>
        )}
        {step === "solo" && (
          <SoloEntries
            roster={roster} competitions={competitions} reference={reference}
            rows={rows} onPatchRow={patchRow}
            onHide={hideDancer} onRestore={restoreDancer}
            showHidden={showHidden} setShowHidden={setShowHidden}
            incompleteIds={incompleteIds} showIncomplete={showIncomplete} setShowIncomplete={setShowIncomplete}
          />
        )}
        {step === "teams" && (
          <Teams
            event={event} roster={roster} competitions={competitions} reference={reference}
            teams={teamRows} savedTeams={savedTeams} onChangeTeams={setTeamRows}
          />
        )}
        {step === "assessment" && (
          <Assessment
            event={event} quote={quote} quoting={quoting}
            reclaimRows={reclaimRows} onChangeReclaim={setReclaimRows}
          />
        )}
        {step === "checkout" && (
          <>
            {payError && <div className="guard-errors" style={{ marginTop: 0 }}>{payError}</div>}
            <CheckoutReview
              event={event} roster={roster} competitions={competitions} reference={reference}
              quote={quote} quoting={quoting}
              payChoice={payChoice} onPayChoice={setPayChoice}
              onEditEntries={() => setStep("solo")} onEditTeams={() => setStep("teams")}
              onEditReclaim={hasAssessmentStep ? () => goStep("assessment") : undefined}
            />
          </>
        )}
      </div>

      <ActionBar
        back={prevStep
          ? { label: prevStep.label, onClick: () => goStep(prevStep.key) }
          : { label: "Event page", onClick: () => navigate(`/oireachtas/${id}`) }}
        status={status}
        next={step === "checkout"
          ? { label: paying ? "Starting payment…" : "Continue to payment", onClick: pay, disabled: paying || quoting || !guardOk || !hasNew || isClosed || !!planError }
          : nextStep
            ? { label: `Next: ${nextStep.label}`, onClick: () => goStep(nextStep.key), disabled: nextDisabled || isClosed }
            : null}
      />
    </div>
  );
}
